export type NotificationSetupState =
  | 'ready'
  | 'granted'
  | 'denied'
  | 'insecure'
  | 'ios-browser'
  | 'unsupported';

const PROMPT_DISMISS_KEY = 'bth_notif_prompt_dismissed';

export function isIosDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isSecurePortalContext(): boolean {
  if (typeof window === 'undefined') return false;
  return window.isSecureContext;
}

export function getNotificationSetupState(): NotificationSetupState {
  if (typeof window === 'undefined') return 'unsupported';

  if (!isSecurePortalContext()) return 'insecure';

  if (!('Notification' in window)) {
    if (isIosDevice() && !isStandaloneDisplay()) return 'ios-browser';
    return 'unsupported';
  }

  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return 'ready';
}

export function canEnableNotifications(state: NotificationSetupState): boolean {
  return state === 'ready';
}

export type NotificationSetupCopy = {
  title: string;
  message: string;
};

export function notificationSetupCopy(state: NotificationSetupState): NotificationSetupCopy {
  switch (state) {
    case 'insecure':
      return {
        title: 'HTTPS required',
        message:
          'Notifications need a secure connection. The address bar shows “Not secure” because the portal is on HTTP. Enable SSL for portal.banteckhan.com.sg in cPanel (AutoSSL / Let’s Encrypt), then open https://portal.banteckhan.com.sg and enable notifications again.',
      };
    case 'ios-browser':
      return {
        title: 'iPhone / iPad alerts',
        message:
          'In Safari: Share → Add to Home Screen, open BTH Portal from your home screen, then tap Enable notifications.',
      };
    case 'denied':
      return {
        title: 'Notifications blocked',
        message:
          'Chrome / Edge: click the icon left of the address bar → Site settings → Notifications → Allow → reload this page. If you use HTTP, fix HTTPS first — browsers often block notifications on “Not secure” sites.',
      };
    case 'unsupported':
      return {
        title: 'Not supported here',
        message:
          'This browser cannot use web notifications. Try Chrome or Edge on desktop, or install the portal to your phone home screen.',
      };
    case 'granted':
      // Intentionally unused — NotificationSetupCard hides when permission is granted.
      return { title: '', message: '' };
    default:
      return {
        title: 'Stay updated',
        message:
          'Enable once on this device for closed-tab reminders. Alerts are limited to portals you are allowed to use.',
      };
  }
}

export function isNotificationPromptDismissed(): boolean {
  try {
    return localStorage.getItem(PROMPT_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissNotificationPrompt(): void {
  try {
    localStorage.setItem(PROMPT_DISMISS_KEY, '1');
  } catch {
    // ignore
  }
}

export function clearNotificationPromptDismissal(): void {
  try {
    localStorage.removeItem(PROMPT_DISMISS_KEY);
  } catch {
    // ignore
  }
}
